import type { MinuteIST, Minutes } from "../types/hr.model";

/**
 * The ONE duration formatter (invariant I3).
 *
 * Everything on the wire is minutes; the approved designs render three
 * different shapes from that single type — "7.2 hrs", "132 min", "+1.5 hrs".
 * Formatting per-screen guarantees they drift, so every caller comes here.
 *
 * null means UNKNOWN and renders "--". 0 is a real zero and renders "0 hrs"
 * (invariant I10) — a tenant with no shift configured has unknown overtime,
 * which is not the same as no overtime.
 */
export const EM_DASH = "--";

/** "7.2 hrs" — one decimal, matching the approved Work Hours cards. */
export function fmtHours(min: Minutes | null | undefined): string {
  if (min == null) return EM_DASH;
  return `${(min / 60).toFixed(1)} hrs`;
}

/** "132 min" — used only where the design shows raw minutes (break time). */
export function fmtMinutes(min: Minutes | null | undefined): string {
  if (min == null) return EM_DASH;
  return `${Math.round(min)} min`;
}

/** "25m" / "1h 10m" — a short gap beside a time (late by, left early by). */
export function fmtShortDuration(min: Minutes | null | undefined): string {
  if (min == null) return EM_DASH;
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * "+1.5 hrs" / "-0.5 hrs" — signed, for overtime.
 * Overtime is signed on the wire: negative means undertime.
 */
export function fmtSignedHours(min: Minutes | null | undefined): string {
  if (min == null) return EM_DASH;
  const sign = min >= 0 ? "+" : "-";
  return `${sign}${(Math.abs(min) / 60).toFixed(1)} hrs`;
}

/** 543 → "09:03 am". Minutes past IST midnight (invariant I2). */
export function fmtClock(minute: MinuteIST | null | undefined): string {
  if (minute == null) return EM_DASH;
  const h24 = Math.floor(minute / 60) % 24;
  const m = Math.abs(Math.round(minute % 60));
  const period = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}

/**
 * Money, from a Decimal STRING (invariant I4).
 *
 * Deliberately NOT `fmtCurrency` from the dashboard-v2 helpers: that abbreviates
 * at ≥1000 ("₹1.2k"), which silently rounds a payroll deduction. A salary
 * figure must be shown exactly as sent, so we never parseFloat it either.
 */
export function fmtMoney(amount: string | null | undefined): string {
  if (amount == null) return EM_DASH;
  const negative = amount.trim().startsWith("-");
  const digits = amount.replace(/^-/, "");
  const [whole, fraction] = digits.split(".");
  const grouped = Number(whole).toLocaleString("en-IN");
  const body = fraction && Number(fraction) > 0 ? `${grouped}.${fraction}` : grouped;
  return `${negative ? "- " : ""}₹${body}`;
}

/** "91%" / "77.3%" — trims a trailing ".0" the way the designs do. */
export function fmtPct(pct: number | null | undefined): string {
  if (pct == null) return EM_DASH;
  const s = pct.toFixed(1);
  return `${s.endsWith(".0") ? s.slice(0, -2) : s}%`;
}

/** "SJ" from "Sarah Johnson"; falls back to one initial for mononyms. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** "1 min ago" / "2 hrs ago" / "1 day ago" — matches the activity feed. */
export function fmtRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((now.getTime() - then) / 1000));
  if (secs < 60) return `${secs || 1} sec ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ${hrs === 1 ? "hr" : "hrs"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

/* ───────────────────────────────── additive helpers (attendance / requests) */

/**
 * 552 → "9:12am" — the compact clock the punch header and status lines use.
 * Same input as `fmtClock` (minutes past IST midnight, invariant I2); only the
 * shape differs, so there is still one rule for turning minutes into a time.
 */
export function fmtClockCompact(minute: MinuteIST | null | undefined): string {
  if (minute == null) return EM_DASH;
  const h24 = Math.floor(minute / 60) % 24;
  const m = Math.abs(Math.round(minute % 60));
  const period = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")}${period}`;
}

/**
 * "0.50" → "0.5", "12.00" → "12", "2.25" → "2.25".
 *
 * Day counts arrive as Decimal STRINGS (invariant I4). Trimming is done on the
 * string — never parseFloat — so a balance of "0.50" can never print as
 * "0.49999" and a large balance keeps every digit.
 */
export function fmtDays(days: string | null | undefined): string {
  if (days == null) return EM_DASH;
  const s = days.trim();
  if (!s) return EM_DASH;
  if (!s.includes(".")) return s;
  const trimmed = s.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed === "" || trimmed === "-" ? "0" : trimmed;
}

/** "1.00" → "1 day", "0.50" → "0.5 days", "3.00" → "3 days". */
export function fmtDaysLabel(days: string | null | undefined): string {
  const n = fmtDays(days);
  if (n === EM_DASH) return EM_DASH;
  return `${n} ${n === "1" ? "day" : "days"}`;
}

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * "2026-01-03" → "3 Jan" (or "3 Jan 2026" with `withYear`).
 *
 * ONLY for bare IsoDate values the server ships without a label (e.g. approval
 * conflicts). Built from the string's own digits — never `new Date("…")`,
 * which parses as UTC midnight and lands on the previous day west of UTC.
 */
export function fmtIsoDay(iso: string | null | undefined, withYear = false): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  if (!m) return iso ?? EM_DASH;
  const month = SHORT_MONTHS[Number(m[2]) - 1] ?? m[2];
  const day = String(Number(m[3]));
  return withYear ? `${day} ${month} ${m[1]}` : `${day} ${month}`;
}

/** Minutes past IST midnight → "HH:mm" for an `<input type="time">`. */
export function minuteToHHmm(minute: MinuteIST | null | undefined): string {
  if (minute == null) return "";
  const h = Math.floor(minute / 60) % 24;
  const m = Math.round(minute % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "HH:mm" (24h) → minutes past IST midnight; null for blank or malformed. */
export function hhmmToMinute(value: string | null | undefined): MinuteIST | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
